import { Alert02Icon, ArrowLeft01Icon, ArrowRight01Icon, BicycleIcon, BubbleChatIcon, BulbIcon, Calendar03Icon, ChartIncreaseIcon, CheckmarkCircle01Icon, Clock01Icon, Coffee02Icon, Coins01Icon, DrinkIcon, File02Icon, LockIcon, Pizza01Icon, RestaurantTableIcon, Shield01Icon, SparklesIcon, Store01Icon, ThumbsUpIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useState } from "react";
import "./TestDigital.scss";

interface QuizState {
  businessType: string;
  orderChannel: string;
  customerData: string;
  priorityGoal: string;
  ownerName: string;
  restaurantName: string;
  whatsapp: string;
  email: string;
}

const INITIAL_STATE: QuizState = {
  businessType: "casual",
  orderChannel: "whatsapp_manual",
  customerData: "none",
  priorityGoal: "commissions",
  ownerName: "",
  restaurantName: "",
  whatsapp: "",
  email: "",
};

export const TestDigitalPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [quizData, setQuizData] = useState<QuizState>(INITIAL_STATE);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const totalSteps = 4;

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Generate results
      setIsGenerating(true);
      setTimeout(() => {
        setIsGenerating(false);
        setShowResults(true);
        setTimeout(() => {
          scrollToSection("reporte");
        }, 100);
      }, 1200);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Dynamic score computation based on their answers
  const computeScore = () => {
    let score = 50;

    // Channel impact
    if (quizData.orderChannel === "web_own") score += 28;
    else if (quizData.orderChannel === "whatsapp_auto") score += 12;
    else if (quizData.orderChannel === "apps_only") score += 6;
    else if (quizData.orderChannel === "whatsapp_manual") score -= 8;

    // Customer data impact
    if (quizData.customerData === "digital_crm") score += 20;
    else if (quizData.customerData === "excel_manual") score += 5;
    else score -= 12;

    return Math.min(Math.max(score, 34), 92);
  };

  const score = computeScore();
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const whatsappMessage = encodeURIComponent(
    `Hola BiteLink, completé el test digital para mi restaurante "${quizData.restaurantName || "mi restaurante"}". Mi score fue de ${score}/100 y me gustaría agendar la sesión de 30 minutos para revisar mi estrategia.`
  );

  return (
    <div className="test-digital-page">
      {/* 1. TOP NAVBAR */}
      <nav className="td-navbar">
        <div className="td-logo-container">
          <span className="td-logo-text">BiteLink</span>
          <span className="td-logo-badge">Diagnóstico IA</span>
        </div>
        <button
          className="td-nav-cta"
          onClick={() => scrollToSection("diagnostico")}
        >
          <span>Comenzar Test</span>
          <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
        </button>
      </nav>

      {/* 2. HERO SECTION */}
      <section className="td-hero-section">
        <div className="td-hero-glow" />
        <div className="td-hero-grid">
          <div>
            <div className="td-pill-badge">
              <HugeiconsIcon icon={SparklesIcon} size={16} />
              <span>Auditoría Gratuita con IA</span>
            </div>
            <h1 className="td-hero-title">
              Descubre cómo llevar <span className="text-amber">tu restaurante</span> al{" "}
              <span className="text-green">siguiente nivel</span> con marketing digital e IA
            </h1>
            <p className="td-hero-desc">
              Cuéntanos qué estás haciendo, qué quieres mejorar y qué metas tienes. Analizamos tu situación
              operativa en tiempo real y te entregamos un diagnóstico con recomendaciones prácticas para
              atraer más comensales y eliminar comisiones innecesarias.
            </p>
            <div>
              <button
                className="td-hero-cta"
                onClick={() => scrollToSection("diagnostico")}
              >
                <span>Comenzar diagnóstico gratuito</span>
                <HugeiconsIcon icon={ArrowRight01Icon} size={20} />
              </button>
            </div>

            <div className="td-trust-bar">
              <div className="td-trust-item">
                <div className="td-trust-icon">
                  <HugeiconsIcon icon={CheckmarkCircle01Icon} size={20} color="#64FCBD" />
                </div>
                <div>
                  <div className="td-trust-title">100% gratuito</div>
                  <div className="td-trust-sub">Sin compromiso</div>
                </div>
              </div>
              <div className="td-trust-item">
                <div className="td-trust-icon">
                  <HugeiconsIcon icon={Clock01Icon} size={20} color="#FFDEAC" />
                </div>
                <div>
                  <div className="td-trust-title">Solo 3 minutos</div>
                  <div className="td-trust-sub">De tu tiempo</div>
                </div>
              </div>
              <div className="td-trust-item">
                <div className="td-trust-icon">
                  <HugeiconsIcon icon={Shield01Icon} size={20} color="#8EB7FF" />
                </div>
                <div>
                  <div className="td-trust-title">Tu información</div>
                  <div className="td-trust-sub">Segura y privada</div>
                </div>
              </div>
            </div>
          </div>

          <div className="td-hero-image-wrap">
            <img
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuAgGIXRKZ6H4xUMdDmI-1I1ieQJ9DxoQvkg4mA1ybB8mxcllNjv2--mdyYvjDGrSwEY9Q08yhPZWSMCb0el9sluwXMjnepmqJhpCneh0nrFWQpUkZ6NSxe8g-zAhcLsaowZ57Sa4J7ozjPWqMS_SJuapwnlX6VQziVFUkRRvm311B2HA-zr9WE3GEWx5biHmCQegKPSvAoD-LaiT4VzTXN8LVmGyiN3K4PLTuDuBF6ZWS7V5FMMSiL068u_avIrTE5Rsts"
              alt="Restaurante digital con IA"
            />
          </div>
        </div>
      </section>

      {/* 3. ¿CÓMO FUNCIONA? */}
      <section className="td-how-it-works">
        <div className="td-section-header">
          <h2>¿Cómo funciona?</h2>
          <p>
            Es muy fácil. Solo tienes que compartir algunos detalles sobre tu negocio y en pocos minutos
            recibirás un plan de acción personalizado.
          </p>
        </div>

        <div className="td-steps-grid">
          <div className="td-step-card">
            <div className="td-step-top">
              <div className="td-step-num">1</div>
              <div className="td-step-icon">
                <HugeiconsIcon icon={File02Icon} size={22} />
              </div>
            </div>
            <h3>Cuéntanos sobre tu negocio</h3>
            <p>
              Completa un formulario sencillo con información clave de tu restaurante y tu estrategia
              actual de venta y canales.
            </p>
          </div>

          <div className="td-step-card">
            <div className="td-step-top">
              <div className="td-step-num" style={{ background: "#FEB700", color: "#382400" }}>2</div>
              <div className="td-step-icon" style={{ background: "#FEF3C7", color: "#B45309" }}>
                <HugeiconsIcon icon={SparklesIcon} size={22} />
              </div>
            </div>
            <h3>Analizamos tu situación</h3>
            <p>
              Revisamos tus datos con algoritmos de IA y detectamos oportunidades de mejora inmediata en tu
              presencia digital y márgenes.
            </p>
          </div>

          <div className="td-step-card">
            <div className="td-step-top">
              <div className="td-step-num" style={{ background: "#007F58" }}>3</div>
              <div className="td-step-icon" style={{ background: "#DCFCE7", color: "#15803D" }}>
                <HugeiconsIcon icon={BulbIcon} size={22} />
              </div>
            </div>
            <h3>Recibes tu diagnóstico</h3>
            <p>
              Te enviamos un informe claro con tus fortalezas, áreas de oportunidad, score de madurez y
              recomendaciones accionables.
            </p>
          </div>

          <div className="td-step-card">
            <div className="td-step-top">
              <div className="td-step-num" style={{ background: "#07154E" }}>4</div>
              <div className="td-step-icon" style={{ background: "#E2E8F0", color: "#07154E" }}>
                <HugeiconsIcon icon={ChartIncreaseIcon} size={22} />
              </div>
            </div>
            <h3>Implementas y creces</h3>
            <p>
              Tú eliges qué aplicar. Nosotros te acompañamos con las herramientas y el soporte estratégico
              paso a paso.
            </p>
          </div>
        </div>
      </section>

      {/* 4. THE INTERACTIVE DIAGNOSTIC QUESTIONNAIRE */}
      <section id="diagnostico" className="td-quiz-section">
        <div className="td-quiz-container">
          <div className="td-section-header" style={{ marginBottom: 30 }}>
            <div className="td-pill-badge" style={{ color: "#015FEE", background: "#DDEAFD", borderColor: "#BFDBFE" }}>
              <HugeiconsIcon icon={File02Icon} size={15} />
              <span>Inicia tu evaluación</span>
            </div>
            <h2>Diagnóstico Interactivo de Madurez Digital</h2>
            <p>
              Responde las siguientes preguntas para calcular tu índice de rendimiento, fugas operativas y
              oportunidades de crecimiento.
            </p>
          </div>

          <div className="td-quiz-card">
            {/* Step Header */}
            <div className="td-quiz-progress-top">
              <span className="td-step-badge">
                Paso {currentStep} de {totalSteps}
              </span>
              <div className="td-quiz-timer">
                <HugeiconsIcon icon={Clock01Icon} size={15} color="#007F58" />
                <span>&lt; 1 min</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="td-progress-bar-bg">
              <div
                className="td-progress-bar-fill"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>

            {/* Step 1: Business Profile */}
            {currentStep === 1 && (
              <div>
                <h3 className="td-quiz-question-title">
                  ¿Qué tipo de concepto gastronómico tienes?
                </h3>
                <p className="td-quiz-question-sub">
                  Esto nos permite ajustar el benchmark y las métricas de tu sector.
                </p>

                <div className="td-options-grid">
                  {[
                    { id: "casual", label: "Restaurante Casual / Salón", desc: "Mesas, comensales presenciales y servicio para llevar", icon: <HugeiconsIcon icon={RestaurantTableIcon} size={20} /> },
                    { id: "fastfood", label: "Fast Food / Pizzería / Burgers", desc: "Enfocado en despacho ágil y alto volumen de pedidos", icon: <HugeiconsIcon icon={Pizza01Icon} size={20} /> },
                    { id: "cafe", label: "Cafetería / Panadería", desc: "Desayunos, repostería y ticket promedio frecuente", icon: <HugeiconsIcon icon={Coffee02Icon} size={20} /> },
                    { id: "bar", label: "Bar / Gastrobar / Cervecería", desc: "Bebidas, piqueos y eventos de fin de semana", icon: <HugeiconsIcon icon={DrinkIcon} size={20} /> },
                  ].map((opt) => (
                    <div
                      key={opt.id}
                      className={`td-option-card ${quizData.businessType === opt.id ? "active" : ""}`}
                      onClick={() => setQuizData({ ...quizData, businessType: opt.id })}
                    >
                      <div className="td-option-top">
                        <div className="td-opt-icon">{opt.icon}</div>
                        <div className="td-option-circle">
                          {quizData.businessType === opt.id && <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} />}
                        </div>
                      </div>
                      <div className="td-option-content">
                        <div className="td-opt-title">{opt.label}</div>
                        <div className="td-opt-desc">{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Ordering Channel & Friction */}
            {currentStep === 2 && (
              <div>
                <h3 className="td-quiz-question-title">
                  ¿Cómo realiza su pedido cuando un comensal interesado llega a tus redes o WhatsApp?
                </h3>
                <p className="td-quiz-question-sub">
                  Selecciona la opción que mejor describa la experiencia de compra de tus clientes hoy.
                </p>

                <div className="td-options-grid">
                  {[
                    {
                      id: "whatsapp_manual",
                      badge: "Madurez: Básica",
                      badgeClass: "badge-neutral",
                      title: "PDF o Foto del Menú",
                      desc: "Enviamos foto o archivo PDF y luego anotamos a mano en libreta o chat.",
                      icon: <HugeiconsIcon icon={File02Icon} size={20} />,
                    },
                    {
                      id: "whatsapp_auto",
                      badge: "Cuello de botella",
                      badgeClass: "badge-warning",
                      title: "WhatsApp con Respuestas Rápidas",
                      desc: "Respuestas predefinidas, pero en horas pico colapsamos tomando datos manuales.",
                      icon: <HugeiconsIcon icon={BubbleChatIcon} size={20} />,
                    },
                    {
                      id: "apps_only",
                      badge: "Alta comisión ~30%",
                      badgeClass: "badge-error",
                      title: "Solo Apps de Delivery",
                      desc: "Derivamos directo al link de Rappi, UberEats o PedidosYa pagando altas comisiones.",
                      icon: <HugeiconsIcon icon={BicycleIcon} size={20} />,
                    },
                    {
                      id: "web_own",
                      badge: "Óptimo",
                      badgeClass: "badge-success",
                      title: "Web Propia sin Comisión",
                      desc: "Plataforma web donde arman su pedido y pagan directo sin instalar apps.",
                      icon: <HugeiconsIcon icon={Store01Icon} size={20} />,
                    },
                  ].map((opt) => (
                    <div
                      key={opt.id}
                      className={`td-option-card ${quizData.orderChannel === opt.id ? "active" : ""}`}
                      onClick={() => setQuizData({ ...quizData, orderChannel: opt.id })}
                    >
                      <div className="td-option-top">
                        <div className="td-opt-icon">{opt.icon}</div>
                        <div className="td-option-circle">
                          {quizData.orderChannel === opt.id && <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} />}
                        </div>
                      </div>
                      <div className="td-option-content">
                        <span className={`td-opt-badge ${opt.badgeClass}`}>{opt.badge}</span>
                        <div className="td-opt-title">{opt.title}</div>
                        <div className="td-opt-desc">{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Customer Data & Loyalty */}
            {currentStep === 3 && (
              <div>
                <h3 className="td-quiz-question-title">
                  ¿Qué haces hoy con los datos y la fidelización de tus clientes?
                </h3>
                <p className="td-quiz-question-sub">
                  El mayor activo de un restaurante es su base de clientes recurrentes.
                </p>

                <div className="td-options-grid">
                  {[
                    {
                      id: "none",
                      badge: "Fuga de clientes",
                      badgeClass: "badge-error",
                      title: "No guardamos datos",
                      desc: "Los pedidos entran y salen; dependemos de volver a pagar anuncios para que regresen.",
                      icon: <HugeiconsIcon icon={Coins01Icon} size={20} />,
                    },
                    {
                      id: "excel_manual",
                      badge: "Semi manual",
                      badgeClass: "badge-warning",
                      title: "Agenda / Excel manual",
                      desc: "Guardamos contactos en la agenda de WhatsApp o tarjetas físicas de sellos.",
                      icon: <HugeiconsIcon icon={File02Icon} size={20} />,
                    },
                    {
                      id: "digital_crm",
                      badge: "Avanzado",
                      badgeClass: "badge-success",
                      title: "Sistema digital con puntos",
                      desc: "Base de datos automatizada donde acumulan puntos o cashback en cada pedido.",
                      icon: <HugeiconsIcon icon={ChartIncreaseIcon} size={20} />,
                    },
                    {
                      id: "want_to_start",
                      badge: "Prioridad 2026/2027",
                      badgeClass: "badge-neutral",
                      title: "Queremos implementarlo ya",
                      desc: "Es nuestra meta principal para el nuevo año pero no tenemos la herramienta.",
                      icon: <HugeiconsIcon icon={SparklesIcon} size={20} />,
                    },
                  ].map((opt) => (
                    <div
                      key={opt.id}
                      className={`td-option-card ${quizData.customerData === opt.id ? "active" : ""}`}
                      onClick={() => setQuizData({ ...quizData, customerData: opt.id })}
                    >
                      <div className="td-option-top">
                        <div className="td-opt-icon">{opt.icon}</div>
                        <div className="td-option-circle">
                          {quizData.customerData === opt.id && <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} />}
                        </div>
                      </div>
                      <div className="td-option-content">
                        <span className={`td-opt-badge ${opt.badgeClass}`}>{opt.badge}</span>
                        <div className="td-opt-title">{opt.title}</div>
                        <div className="td-opt-desc">{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Contact & Goals */}
            {currentStep === 4 && (
              <div>
                <h3 className="td-quiz-question-title">
                  ¿Dónde te enviamos tu reporte personalizado?
                </h3>
                <p className="td-quiz-question-sub">
                  Ingresa tus datos para generar el informe y desbloquear las recomendaciones con IA.
                </p>

                <div className="td-form-fields">
                  <div className="td-input-wrap">
                    <label>Tu Nombre</label>
                    <input
                      type="text"
                      placeholder="Ej. Carlos Martínez"
                      value={quizData.ownerName}
                      onChange={(e) => setQuizData({ ...quizData, ownerName: e.target.value })}
                    />
                  </div>
                  <div className="td-input-wrap">
                    <label>Nombre de tu Restaurante</label>
                    <input
                      type="text"
                      placeholder="Ej. La Brasería Urbana"
                      value={quizData.restaurantName}
                      onChange={(e) => setQuizData({ ...quizData, restaurantName: e.target.value })}
                    />
                  </div>
                  <div className="td-input-wrap">
                    <label>WhatsApp / Teléfono</label>
                    <input
                      type="tel"
                      placeholder="Ej. +505 8888 8888"
                      value={quizData.whatsapp}
                      onChange={(e) => setQuizData({ ...quizData, whatsapp: e.target.value })}
                    />
                  </div>
                  <div className="td-input-wrap">
                    <label>Correo Electrónico</label>
                    <input
                      type="email"
                      placeholder="tu@restaurante.com"
                      value={quizData.email}
                      onChange={(e) => setQuizData({ ...quizData, email: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="td-quiz-actions">
              {currentStep > 1 && (
                <button type="button" className="td-btn-prev" onClick={handlePrev}>
                  <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
                  <span>Anterior</span>
                </button>
              )}
              <button
                type="button"
                className={`td-btn-next ${currentStep === totalSteps ? "btn-generate" : ""}`}
                onClick={handleNext}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <span>Generando tu informe con IA...</span>
                ) : currentStep === totalSteps ? (
                  <>
                    <span>Generar mi Diagnóstico con IA</span>
                    <HugeiconsIcon icon={SparklesIcon} size={18} />
                  </>
                ) : (
                  <>
                    <span>Siguiente pregunta</span>
                    <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
                  </>
                )}
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: 20, color: "#64748B", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <HugeiconsIcon icon={LockIcon} size={15} color="#007F58" />
              <span>Tus datos están 100% seguros y nunca serán compartidos</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. DIAGNOSTIC RESULTS & BENCHMARK REPORT */}
      {(showResults || currentStep === 4) && (
        <section id="reporte" className="td-results-section">
          <div className="td-results-card">
            <div style={{ maxWidth: 700, marginBottom: 36 }}>
              <div className="td-pill-badge" style={{ color: "#B45309", background: "#FEF3C7", borderColor: "#FDE68A" }}>
                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={15} />
                <span>Tu Reporte Personalizado</span>
              </div>
              <h2 style={{ fontSize: 32, fontWeight: 800, color: "#0C1B33", marginBottom: 10 }}>
                Diagnóstico de {quizData.restaurantName || "tu Restaurante"}
              </h2>
              <p style={{ color: "#4A5568", fontSize: 16 }}>
                Posición real frente a competidores directos, fugas operativas de margen y hoja de ruta táctica.
              </p>
            </div>

            <div className="td-results-grid">
              {/* Score Gauge */}
              <div className="td-gauge-box">
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14 }}>
                  <HugeiconsIcon icon={ChartIncreaseIcon} size={18} color="#015FEE" />
                  <span>Tu Diagnóstico Digital</span>
                </div>

                <div className="td-gauge-circle">
                  <svg viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke="#D7E3FF"
                      strokeWidth="10"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke="#007F58"
                      strokeWidth="10"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
                    />
                  </svg>
                  <div className="td-gauge-text">
                    <span className="td-score-val">{score}</span>
                    <span className="td-score-total">/ 100 pts</span>
                  </div>
                </div>

                <div className="td-score-status">
                  <div className="td-score-title">Nivel de madurez digital</div>
                  <span className="td-status-pill">
                    {score < 55 ? "Operación con Fugas Críticas" : "Bueno, con oportunidades"}
                  </span>
                </div>

                <div className="td-benchmark-box">
                  <span>Promedio de tu zona: <strong>54 pts</strong></span>
                  <span className="td-tag-pos">
                    {score >= 54 ? `+${score - 54} pts vs promedio` : `${score - 54} pts vs promedio`}
                  </span>
                </div>
              </div>

              {/* Strengths & Weaknesses */}
              <div className="td-feedback-col">
                <div className="td-feedback-group">
                  <div className="td-fb-header good">
                    <HugeiconsIcon icon={ThumbsUpIcon} size={18} />
                    <span>Lo que estás haciendo bien</span>
                  </div>
                  <div className="td-fb-item item-good">
                    <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>Iniciativa de evaluar tu negocio antes del cierre de año para anticipar la cuesta de enero.</span>
                  </div>
                  <div className="td-fb-item item-good">
                    <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>Tráfico activo de clientes interesados a través de canales digitales y recomendaciones.</span>
                  </div>
                </div>

                <div className="td-feedback-group">
                  <div className="td-fb-header bad">
                    <HugeiconsIcon icon={Alert02Icon} size={18} />
                    <span>Fugas de conversión detectadas</span>
                  </div>
                  {quizData.orderChannel === "apps_only" && (
                    <div className="td-fb-item item-bad">
                      <HugeiconsIcon icon={Alert02Icon} size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span><strong>Dependencia de apps de delivery:</strong> Pierdes hasta un 28%-30% de tu margen neto en comisiones de terceros.</span>
                    </div>
                  )}
                  {quizData.orderChannel === "whatsapp_manual" && (
                    <div className="td-fb-item item-bad">
                      <HugeiconsIcon icon={Alert02Icon} size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span><strong>Freno del PDF en WhatsApp:</strong> Hasta un 40% de clientes abandonan si tienen que descargar un archivo y esperar respuesta.</span>
                    </div>
                  )}
                  {quizData.customerData === "none" && (
                    <div className="td-fb-item item-bad">
                      <HugeiconsIcon icon={Alert02Icon} size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span><strong>Cero base de datos propia:</strong> Cada comensal que compra se va sin que puedas recontactarlo sin volver a pagar pauta.</span>
                    </div>
                  )}
                  <div className="td-fb-item item-bad">
                    <HugeiconsIcon icon={Alert02Icon} size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span><strong>Cuello de botella de tiempo:</strong> Múltiples horas al mes respondiendo precios y tomando pedidos a mano.</span>
                  </div>
                </div>
              </div>

              {/* Recommendations & Action Plan */}
              <div className="td-recommendations-col">
                <div className="td-rec-header">
                  <HugeiconsIcon icon={BulbIcon} size={20} color="#015FEE" />
                  <span>Recomendaciones Estratégicas</span>
                </div>

                <div className="td-rec-item">
                  <div className="td-rec-icon">
                    <HugeiconsIcon icon={Store01Icon} size={18} />
                  </div>
                  <div>
                    <div className="td-rec-title">Activa tu catálogo web directo</div>
                    <div className="td-rec-desc">Vende sin comisiones de terceros en una web app sin descargas y quédate el margen.</div>
                  </div>
                </div>

                <div className="td-rec-item">
                  <div className="td-rec-icon" style={{ background: "#FEF3C7", color: "#B45309" }}>
                    <HugeiconsIcon icon={Coins01Icon} size={18} />
                  </div>
                  <div>
                    <div className="td-rec-title">Crea un programa de fidelización con puntos</div>
                    <div className="td-rec-desc">+35% recompra en 60 días ofreciendo beneficios por pedir directamente contigo.</div>
                  </div>
                </div>

                <div className="td-rec-item">
                  <div className="td-rec-icon" style={{ background: "#DCFCE7", color: "#15803D" }}>
                    <HugeiconsIcon icon={BubbleChatIcon} size={18} />
                  </div>
                  <div>
                    <div className="td-rec-title">Automatiza tus pedidos en WhatsApp</div>
                    <div className="td-rec-desc">Los clientes arman su pedido y a ti te llega listo a cocina sin intervención manual.</div>
                  </div>
                </div>

                {/* ROI Card */}
                <div className="td-roi-card">
                  <span className="td-roi-title">Tu Potencial Estimado</span>
                  <div className="td-roi-row">
                    <span>Incremento de facturación:</span>
                    <span className="val-green">+25% a +40%</span>
                  </div>
                  <div className="td-roi-row">
                    <span>Ahorro en comisiones:</span>
                    <span className="val-blue">~$650 USD / mes</span>
                  </div>
                </div>

                {/* Booking CTA */}
                <a
                  href={`https://wa.me/?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noreferrer"
                  className="td-btn-book"
                >
                  <HugeiconsIcon icon={Calendar03Icon} size={18} />
                  <span>Agendar Asesoría Gratuita de 30 Minutos</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 6. BOTTOM CTA BANNER */}
      <section className="td-bottom-cta">
        <div className="td-cta-inner">
          <span className="td-cta-pill">Da el primer paso</span>
          <h2>
            Tu próximo comensal ya está en línea. <span>Asegúrate de que te encuentre.</span>
          </h2>
          <p>
            Completa el formulario en 3 minutos y recibe gratis tu diagnóstico personalizado de marketing y
            madurez digital para tu restaurante.
          </p>
          <button
            className="td-cta-button"
            onClick={() => scrollToSection("diagnostico")}
          >
            <span>Obtener mi diagnóstico gratuito</span>
            <HugeiconsIcon icon={ArrowRight01Icon} size={20} />
          </button>
          <div className="td-cta-note">
            <HugeiconsIcon icon={LockIcon} size={15} color="#64FCBD" />
            <span>Acceso inmediato · Informe confidencial para tu restaurante</span>
          </div>
        </div>
      </section>

      {/* 7. FOOTER */}
      <footer className="td-footer">
        <div className="td-footer-inner">
          <div className="td-footer-top">
            <div>
              <span className="td-footer-brand">BiteLink</span>
              <p style={{ color: "#64748B", fontSize: 13, marginTop: 4, maxWidth: 400 }}>
                Tecnología y marketing digital accesible para impulsar restaurantes, cafeterías y negocios
                gastronómicos sin comisiones abusivas.
              </p>
            </div>
            <div className="td-footer-links">
              <a href="#diagnostico" onClick={() => scrollToSection("diagnostico")}>
                Diagnóstico IA
              </a>
              <a href="/legal/privacy">Privacidad</a>
              <a href="/legal/terms">Términos y Condiciones</a>
            </div>
          </div>
          <div className="td-footer-bottom">
            <span>© 2025-2026 BiteLink. Todos los derechos reservados.</span>
            <span>Plataforma Gastronómica Optimizada</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TestDigitalPage;
